/**
 * Administración del catálogo.
 *
 * Pensado para que el dueño cargue su carta sin ayuda: agregar una categoría,
 * agregar productos con su precio, corregir uno que subió de valor.
 *
 * Dos decisiones que se notan al usarlo:
 *
 *  • Los cambios van directo al servidor, no al outbox. Es configuración, no
 *    servicio: se hace con el local vacío. Sin conexión se avisa y no se
 *    finge que quedó guardado.
 *
 *  • Al guardar se vuelve a bajar el catálogo completo. Es una llamada de más,
 *    pero garantiza que lo que quedó en IndexedDB es exactamente lo que tiene
 *    el servidor, en vez de un espejo armado a mano que puede desviarse.
 */

import { useState } from 'react';
import * as api from '../api/endpoints';
import { ErrorApi, ErrorRed } from '../api/cliente';
import { bajarCatalogo } from '../datos/sync';
import { cop } from '../lib/formato';
import type { CatalogoVista } from '../datos/vistas';
import type { Producto } from '../tipos/contrato';

interface Props {
  catalogo: CatalogoVista | undefined;
  onCerrar: () => void;
  onAviso: (texto: string) => void;
}

/** Campos del formulario. El precio se maneja como texto mientras se escribe. */
interface Borrador {
  id: string | null;
  nombre: string;
  precio: string;
  categoriaId: string;
  requiereCocina: boolean;
}

const VACIO: Borrador = {
  id: null,
  nombre: '',
  precio: '',
  categoriaId: '',
  requiereCocina: true,
};

export function ModalCatalogo({ catalogo, onCerrar, onAviso }: Props) {
  const [borrador, setBorrador] = useState<Borrador>(VACIO);
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [filtro, setFiltro] = useState<string>('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categorias = catalogo?.categorias ?? [];
  const productos = catalogo?.productos ?? [];

  const visibles = filtro ? productos.filter((p) => p.categoriaId === filtro) : productos;

  /** Traduce cualquier fallo a algo que el dueño pueda accionar. */
  const manejarError = (e: unknown) => {
    if (e instanceof ErrorRed) {
      setError('Sin conexión. El catálogo se administra en línea; los cambios no se guardaron.');
    } else if (e instanceof ErrorApi) {
      setError(e.message);
    } else {
      setError('No se pudo guardar.');
    }
  };

  const guardarProducto = async () => {
    const precio = Number(borrador.precio.replace(/[^\d]/g, ''));

    if (!borrador.nombre.trim()) return setError('Ponele un nombre al producto.');
    if (!Number.isFinite(precio) || precio <= 0) return setError('El precio debe ser mayor a cero.');

    setError(null);
    setGuardando(true);
    try {
      const datos = {
        nombre: borrador.nombre.trim(),
        precio,
        categoriaId: borrador.categoriaId || null,
        requiereCocina: borrador.requiereCocina,
      };

      if (borrador.id) await api.actualizarProducto(borrador.id, datos);
      else await api.crearProducto(datos);

      await bajarCatalogo();
      setBorrador(VACIO);
      onAviso(borrador.id ? 'Producto actualizado' : `"${datos.nombre}" agregado a la carta`);
    } catch (e) {
      manejarError(e);
    } finally {
      setGuardando(false);
    }
  };

  const guardarCategoria = async () => {
    const nombre = nuevaCategoria.trim();
    if (!nombre) return;

    setError(null);
    setGuardando(true);
    try {
      const c = await api.crearCategoria({ nombre, orden: categorias.length + 1 });
      await bajarCatalogo();
      setNuevaCategoria('');
      // Se preselecciona: quien crea una categoría casi siempre va a cargarle
      // productos a continuación.
      setBorrador((b) => ({ ...b, categoriaId: c.id }));
      onAviso(`Categoría "${nombre}" creada`);
    } catch (e) {
      manejarError(e);
    } finally {
      setGuardando(false);
    }
  };

  const alternarDisponible = async (p: Producto) => {
    try {
      await api.cambiarDisponibilidad(p.id, !p.disponible);
      await bajarCatalogo();
      onAviso(p.disponible ? `"${p.nombre}" marcado agotado` : `"${p.nombre}" disponible de nuevo`);
    } catch (e) {
      manejarError(e);
    }
  };

  const eliminar = async (p: Producto) => {
    if (!confirm(`¿Quitar "${p.nombre}" de la carta?`)) return;
    try {
      await api.eliminarProducto(p.id);
      await bajarCatalogo();
      if (borrador.id === p.id) setBorrador(VACIO);
      onAviso(`"${p.nombre}" quitado de la carta`);
    } catch (e) {
      manejarError(e);
    }
  };

  const editar = (p: Producto) => {
    setError(null);
    setBorrador({
      id: p.id,
      nombre: p.nombre,
      precio: String(Math.round(Number(p.precio))),
      categoriaId: p.categoriaId ?? '',
      requiereCocina: p.requiereCocina,
    });
  };

  return (
    <div className="overlay on" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 900 }}>
        <header>
          <h2>Carta</h2>
          <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {productos.length} productos · {categorias.length} categorías
          </span>
          <button className="close" onClick={onCerrar}>
            ✕
          </button>
        </header>

        <div className="body">
          {error && <div className="error-caja">{error}</div>}

          {/* ── Alta / edición ────────────────────────────────────────── */}
          <div
            style={{
              border: '1px solid var(--line)',
              borderRadius: 14,
              padding: 16,
              background: 'var(--bg)',
              marginBottom: 18,
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              {borrador.id ? 'Editando producto' : 'Nuevo producto'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div className="field">
                <label htmlFor="pnombre">Nombre</label>
                <input
                  id="pnombre"
                  value={borrador.nombre}
                  placeholder="Ej. Croissant de almendra"
                  onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })}
                />
              </div>

              <div className="field">
                <label htmlFor="pprecio">Precio</label>
                <input
                  id="pprecio"
                  className="mono"
                  inputMode="numeric"
                  value={borrador.precio}
                  placeholder="11000"
                  /* Se filtran los no-dígitos al escribir: pegar "16.500" o
                     "$16,500" desde una lista debe funcionar sin pelear. */
                  onChange={(e) =>
                    setBorrador({ ...borrador, precio: e.target.value.replace(/[^\d]/g, '') })
                  }
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div className="field">
                <label htmlFor="pcat">Categoría</label>
                <select
                  id="pcat"
                  value={borrador.categoriaId}
                  onChange={(e) => setBorrador({ ...borrador, categoriaId: e.target.value })}
                >
                  <option value="">Sin categoría</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Preparación</label>
                <label className="tip" style={{ paddingTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={borrador.requiereCocina}
                    onChange={(e) =>
                      setBorrador({ ...borrador, requiereCocina: e.target.checked })
                    }
                  />
                  <span className="switch" />
                  <span style={{ fontSize: 12 }}>Va a cocina</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 9, marginTop: 4 }}>
              <button
                className="btn btn-gold"
                onClick={() => void guardarProducto()}
                disabled={guardando}
              >
                {guardando ? 'Guardando…' : borrador.id ? 'Guardar cambios' : '+ Agregar producto'}
              </button>
              {borrador.id && (
                <button className="btn btn-ghost" onClick={() => setBorrador(VACIO)}>
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* ── Categorías ────────────────────────────────────────────── */}
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Categorías
          </div>
          <div className="chips" style={{ marginBottom: 10 }}>
            <button
              className="chip"
              aria-pressed={filtro === ''}
              onClick={() => setFiltro('')}
            >
              Todas
            </button>
            {categorias.map((c) => (
              <button
                key={c.id}
                className="chip"
                aria-pressed={filtro === c.id}
                onClick={() => setFiltro(c.id)}
              >
                {c.nombre}
              </button>
            ))}
          </div>

          <div className="search" style={{ marginBottom: 18 }}>
            <input
              value={nuevaCategoria}
              placeholder="Nueva categoría… (ej. Desayunos)"
              onChange={(e) => setNuevaCategoria(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void guardarCategoria()}
            />
            <button
              className="btn btn-ghost"
              onClick={() => void guardarCategoria()}
              disabled={guardando || !nuevaCategoria.trim()}
            >
              + Categoría
            </button>
          </div>

          {/* ── Listado ───────────────────────────────────────────────── */}
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Productos {filtro && `· ${categorias.find((c) => c.id === filtro)?.nombre}`}
          </div>

          {visibles.length === 0 ? (
            <p className="none">
              No hay productos en esta categoría todavía. Cargá el primero con el formulario de
              arriba.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {visibles.map((p) => (
                <div
                  key={p.id}
                  className="pago-item"
                  style={{ opacity: p.disponible ? 1 : 0.5 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ fontSize: 13 }}>{p.nombre}</b>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {categorias.find((c) => c.id === p.categoriaId)?.nombre ?? 'Sin categoría'}
                      {!p.disponible && ' · agotado'}
                    </div>
                  </div>
                  <span className="mono" style={{ fontWeight: 600, marginRight: 10 }}>
                    {cop(Number(p.precio))}
                  </span>
                  <button
                    className="iconbtn"
                    title={p.disponible ? 'Marcar agotado' : 'Marcar disponible'}
                    onClick={() => void alternarDisponible(p)}
                  >
                    {p.disponible ? '◉' : '○'}
                  </button>
                  <button className="iconbtn" title="Editar" onClick={() => editar(p)}>
                    ✎
                  </button>
                  <button className="x" title="Quitar" onClick={() => void eliminar(p)}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer>
          <div style={{ flex: 1, fontSize: 11.5, color: 'var(--text-secondary)' }}>
            Los cambios se guardan en el servidor al instante.
          </div>
          <button className="btn btn-primary" onClick={onCerrar}>
            Listo
          </button>
        </footer>
      </div>
    </div>
  );
}
